'use client';

import { useAppRouter } from '../../../../shared/router/AppRouterContext';
import { appPath } from '../../../../shared/router/appPath';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';

import { usePrefersReducedMotion, useTypewriter } from '@vs-shop/headless/typewriter';
import {
  SearchCombobox,
  SearchComboboxArrow,
  SearchComboboxBackdrop,
  SearchComboboxClose,
  SearchComboboxContent,
  SearchComboboxForm,
  SearchComboboxInput,
  SearchComboboxList,
  SearchComboboxPortal,
  SearchComboboxPositioner,
  SearchComboboxSubmit,
  useSearchInteraction,
} from '@vs-shop/ui/searchCombobox';

import { Url } from '../../../../route/router';
import { searchSuggestDebounceMs } from '../../../../config/search';

import { getSearchPlaceholder } from './searchPlaceholder';
import SearchSuggestResults from '../../search/SearchSuggestResults';
import { SearchSuggestSkeleton } from '../../../server/search/searchSkeletons';
import type { SuggestHit } from '../../../../effect/schema/openapi/backend';

type Props = {
  initialQuery: string;
};

// A full-text search needs a nonblank query before it can route to results.
const canOpen = (query: string) => query.trim().length > 0;

export default function Search({ initialQuery }: Props) {
  const {
    inputValue: value,
    open,
    onInputValueChange,
    onOpenChange,
    close,
  } = useSearchInteraction(initialQuery);

  const intl = useIntl();
  const router = useAppRouter();

  const prefersReducedMotion = usePrefersReducedMotion();

  // Fire suggest from the 1st character, debounced. An empty (trimmed) input
  // keeps the popup shut, so no query is ever sent for it.
  const [debouncedValue] = useDebounceValue(value, searchSuggestDebounceMs);
  const suggestQuery = debouncedValue.trim();

  // Memoize so `typewriterPhrases` keeps a stable reference across renders.
  const { staticPlaceholder, typewriterPrefix, typewriterPhrases } = useMemo(
    () => getSearchPlaceholder(intl),
    [intl]
  );

  // The animated placeholder is only shown on an empty input with motion allowed;
  // gate the hook on the same condition so it schedules no timers otherwise.
  const animatePlaceholder = !prefersReducedMotion && value.length === 0;

  const animatedPlaceholder = useTypewriter({
    phrases: typewriterPhrases,
    prefix: `${typewriterPrefix.trimEnd()} `,
    enabled: animatePlaceholder,
  });

  const placeholder = animatePlaceholder ? animatedPlaceholder : staticPlaceholder;

  return (
    <SearchCombobox<SuggestHit>
      open={open}
      onOpenChange={onOpenChange}
      inputValue={value}
      onInputValueChange={onInputValueChange}
    >
      <SearchComboboxForm
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = value.trim();
          if (!canOpen(value)) return;
          close();
          router.push(appPath({ route: Url.search, searchParams: { q: trimmed } }));
        }}
      >
        <SearchComboboxInput
          name="q"
          aria-label={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.input',
            defaultMessage: 'Vyhledávání',
          })}
          placeholder={placeholder}
        />
        <SearchComboboxSubmit
          aria-label={intl.formatMessage({
            id: 'component.client.navigation.header.searchPopover.submit',
            defaultMessage: 'Hledat',
          })}
        />
      </SearchComboboxForm>
      <SearchComboboxPortal>
        <SearchComboboxBackdrop />
        <SearchComboboxPositioner>
          <SearchComboboxArrow />
          <SearchComboboxContent>
            <SearchComboboxClose
              aria-label={intl.formatMessage({
                id: 'component.client.navigation.header.searchPopover.close',
                defaultMessage: 'Zavřít vyhledávání',
              })}
              onClick={close}
            />
            <SearchComboboxList>
              {suggestQuery.length === 0 ? (
                // Open, but the debounce has not caught up with the first
                // keystrokes yet — the same placeholders the suggest view shows
                // while its request is in flight, so the panel never flashes empty.
                <SearchSuggestSkeleton />
              ) : (
                <SearchSuggestResults query={suggestQuery} onSelectAction={close} />
              )}
            </SearchComboboxList>
          </SearchComboboxContent>
        </SearchComboboxPositioner>
      </SearchComboboxPortal>
    </SearchCombobox>
  );
}
